/**
 * Hardware Integration Service
 * Supports: Epson Thermal Printer (ESC/POS), Barcode Scanner, Customer Display, Cash Drawer
 * Uses Web Serial API for direct printer/drawer communication
 */

// ============ CONFIG ============
const HW_STORAGE_KEY = 'pharmxai_hardware_config';

export function getHardwareConfig() {
  try {
    return JSON.parse(localStorage.getItem(HW_STORAGE_KEY) || '{}');
  } catch {
    return {};
  }
}

export function saveHardwareConfig(config) {
  localStorage.setItem(HW_STORAGE_KEY, JSON.stringify(config));
}

// ============ WEB SERIAL API SUPPORT CHECK ============
export function isWebSerialSupported() {
  return 'serial' in navigator;
}

// ============ ESC/POS COMMAND BUILDER ============
const ESC = 0x1B;
const GS = 0x1D;
const LF = 0x0A;

const ESCPOS = {
  INIT:           [ESC, 0x40],                       // Initialize printer
  ALIGN_LEFT:     [ESC, 0x61, 0x00],
  ALIGN_CENTER:   [ESC, 0x61, 0x01],
  ALIGN_RIGHT:    [ESC, 0x61, 0x02],
  BOLD_ON:        [ESC, 0x45, 0x01],
  BOLD_OFF:       [ESC, 0x45, 0x00],
  DOUBLE_ON:      [ESC, 0x21, 0x30],                 // Double width+height
  DOUBLE_OFF:     [ESC, 0x21, 0x00],
  UNDERLINE_ON:   [ESC, 0x2D, 0x01],
  UNDERLINE_OFF:  [ESC, 0x2D, 0x00],
  FONT_A:         [ESC, 0x4D, 0x00],                 // Normal 12x24
  FONT_B:         [ESC, 0x4D, 0x01],                 // Condensed 9x17
  CUT_PAPER:      [GS, 0x56, 0x00],                  // Full cut
  CUT_PARTIAL:    [GS, 0x56, 0x01],                  // Partial cut
  FEED_LINES:     (n) => [ESC, 0x64, n],             // Feed n lines
  CASH_DRAWER_1:  [ESC, 0x70, 0x00, 0x19, 0xFA],    // Pin 2 kick
  CASH_DRAWER_2:  [ESC, 0x70, 0x01, 0x19, 0xFA],    // Pin 5 kick
  LINE:           () => new Array(48).fill(0x2D),     // 48 dashes for 80mm
  DLINE:          () => new Array(48).fill(0x3D),     // 48 equals
};

function textToBytes(text) {
  return Array.from(new TextEncoder().encode(text));
}

function padRight(str, len) {
  return str.length >= len ? str.slice(0, len) : str + ' '.repeat(len - str.length);
}

function padLeft(str, len) {
  return str.length >= len ? str.slice(0, len) : ' '.repeat(len - str.length) + str;
}

function formatLine(left, right, width = 48) {
  const rightLen = right.length;
  const leftMax = width - rightLen - 1;
  return padRight(left, leftMax) + ' ' + right;
}

function buildQrBytes(value) {
  const data = textToBytes(value);
  const storeLength = data.length + 3;
  const pL = storeLength & 0xff;
  const pH = (storeLength >> 8) & 0xff;
  return [
    GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06,
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31,
    GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, ...data,
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30,
  ];
}

// ============ THERMAL PRINTER ============
let printerPort = null;
let printerWriter = null;

export async function connectThermalPrinter() {
  if (!isWebSerialSupported()) {
    throw new Error('Web Serial API not supported. Use Chrome/Edge browser.');
  }
  try {
    const port = await navigator.serial.requestPort({
      filters: [
        { usbVendorId: 0x04B8 },  // Epson
        { usbVendorId: 0x0519 },  // Star Micronics
        { usbVendorId: 0x0DD4 },  // Custom
      ]
    });
    await port.open({ baudRate: 9600 });
    printerPort = port;
    printerWriter = port.writable.getWriter();
    saveHardwareConfig({ ...getHardwareConfig(), thermalPrinterConnected: true });
    return true;
  } catch (err) {
    if (err.name === 'NotFoundError') return false; // User cancelled
    throw err;
  }
}

export async function disconnectThermalPrinter() {
  try {
    if (printerWriter) { printerWriter.releaseLock(); printerWriter = null; }
    if (printerPort) { await printerPort.close(); printerPort = null; }
    saveHardwareConfig({ ...getHardwareConfig(), thermalPrinterConnected: false });
  } catch { /* ignore */ }
}

export function isThermalPrinterConnected() {
  return printerPort !== null && printerWriter !== null;
}

async function sendToThermalPrinter(data) {
  if (!printerWriter) throw new Error('Thermal printer not connected');
  await printerWriter.write(new Uint8Array(data));
}

// Build ESC/POS receipt from sale data
export function buildReceiptBytes(sale, branding = {}) {
  const bytes = [];
  const push = (...b) => b.forEach(a => Array.isArray(a) ? bytes.push(...a) : bytes.push(a));
  const text = (t) => push(...textToBytes(t), LF);
  const line = () => push(...ESCPOS.LINE(), LF);
  const dline = () => push(...ESCPOS.DLINE(), LF);

  const name = branding.name || 'DutiLyne';
  const address = branding.address || '';
  const phone = branding.phone || '';

  // Init
  push(...ESCPOS.INIT);

  // Header
  push(...ESCPOS.ALIGN_CENTER);
  push(...ESCPOS.DOUBLE_ON);
  text(name);
  push(...ESCPOS.DOUBLE_OFF);
  if (address) { push(...ESCPOS.FONT_B); text(address); }
  if (phone) text(`Tel: ${phone}`);
  push(...ESCPOS.FONT_A);
  dline();

  // Sale info
  push(...ESCPOS.ALIGN_LEFT);
  text(`Receipt: ${sale.sale_id || 'N/A'}`);
  text(`Date: ${new Date(sale.created_at || Date.now()).toLocaleString()}`);
  if (sale.customer_name) text(`Customer: ${sale.customer_name}`);
  if (sale.cashier_name) text(`Cashier: ${sale.cashier_name}`);
  line();

  // Column headers
  push(...ESCPOS.BOLD_ON);
  text(formatLine('ITEM', 'AMOUNT'));
  push(...ESCPOS.BOLD_OFF);
  line();

  // Items
  (sale.items || []).forEach(item => {
    const itemName = item.product_name || 'Unknown';
    const amount = (item.quantity * item.unit_price).toFixed(2);
    // Product name (may wrap)
    if (itemName.length > 30) {
      text(itemName.slice(0, 48));
      text(formatLine(`  ${item.quantity} x ${item.unit_price.toFixed(2)}`, amount));
    } else {
      text(itemName);
      text(formatLine(`  ${item.quantity} x ${item.unit_price.toFixed(2)}`, amount));
    }
  });

  line();

  // Totals
  push(...ESCPOS.BOLD_ON);
  if (sale.subtotal && sale.subtotal !== sale.total) {
    text(formatLine('SUBTOTAL:', sale.subtotal.toFixed(2)));
    if (sale.discount > 0) {
      text(formatLine('DISCOUNT:', `-${sale.discount.toFixed(2)}`));
    }
  }
  push(...ESCPOS.DOUBLE_ON);
  text(formatLine('TOTAL:', (sale.total || 0).toFixed(2)));
  push(...ESCPOS.DOUBLE_OFF);
  push(...ESCPOS.BOLD_OFF);

  // Payment
  text(formatLine('Payment:', (sale.payment_method || 'cash').toUpperCase()));
  if (sale.momo_reference) text(formatLine('Ref:', sale.momo_reference));

  dline();

  // Footer
  push(...ESCPOS.ALIGN_CENTER);
  push(...ESCPOS.FONT_B);
  text('Thank you for your patronage!');
  text('Get well soon.');
  const portalUrl = sale.portal_qr_target_url || (sale.portal_path && typeof window !== 'undefined'
    ? `${window.location.origin}${sale.portal_path}?source=qr`
    : sale.portal_url);
  if (portalUrl) {
    push(...ESCPOS.FONT_A);
    text('Scan for refills & pharmacist help');
    push(...buildQrBytes(portalUrl));
    push(LF);
    if (sale.portal_branch_name) text(sale.portal_branch_name);
  }
  push(...ESCPOS.FONT_A);

  // Feed and cut
  push(...ESCPOS.FEED_LINES(4));
  push(...ESCPOS.CUT_PARTIAL);

  return bytes;
}

export async function printThermalESCPOS(sale) {
  const branding = getPharmacyBranding();
  const bytes = buildReceiptBytes(sale, branding);
  await sendToThermalPrinter(bytes);
}

// Fallback: browser print with thermal layout
export function printThermalBrowser(sale) {
  // Uses the existing invoiceUtils thermal receipt (browser-based print)
  const { printThermalReceipt } = require('./invoiceUtils');
  printThermalReceipt(sale);
}

// Smart print: Use ESC/POS if connected, else browser print
export async function smartPrintReceipt(sale) {
  if (isThermalPrinterConnected()) {
    try {
      await printThermalESCPOS(sale);
      return { method: 'escpos', success: true };
    } catch (err) {
      console.error('ESC/POS print failed, falling back to browser:', err);
      printThermalBrowser(sale);
      return { method: 'browser', success: true, fallback: true };
    }
  } else {
    printThermalBrowser(sale);
    return { method: 'browser', success: true };
  }
}

// ============ CASH DRAWER ============
export async function openCashDrawer() {
  if (isThermalPrinterConnected()) {
    // Cash drawer connected via printer RJ11
    await sendToThermalPrinter([...ESCPOS.CASH_DRAWER_1]);
    return { method: 'escpos', success: true };
  }
  // Fallback: try to connect printer first
  throw new Error('Cash drawer requires thermal printer connection (RJ11). Connect printer first.');
}

export async function openCashDrawerSafe() {
  try {
    if (isThermalPrinterConnected()) {
      await openCashDrawer();
      return true;
    }
  } catch (err) {
    console.warn('Cash drawer open failed:', err);
  }
  return false;
}

// ============ DESKTOP PRINTER ============
export function printDesktopInvoice(sale) {
  const { printProfessionalInvoice } = require('./invoiceUtils');
  printProfessionalInvoice(sale);
}

// ============ BARCODE SCANNER ============
// USB barcode scanners act as keyboard input — they type characters rapidly then press Enter
// We detect this pattern: multiple chars within 50ms bursts, ending with Enter

let barcodeBuffer = '';
let barcodeTimer = null;
let barcodeCallback = null;

export function startBarcodeListener(onScan) {
  barcodeCallback = onScan;

  const handler = (e) => {
    // Ignore if focus is on an input/textarea (let user type normally)
    const tag = document.activeElement?.tagName;
    const isSearchInput = document.activeElement?.getAttribute('data-testid') === 'pos-search-input';

    // For the POS search input, capture barcode scans
    if (tag === 'INPUT' || tag === 'TEXTAREA') {
      if (!isSearchInput) return;
    }

    if (e.key === 'Enter' && barcodeBuffer.length >= 4) {
      e.preventDefault();
      const code = barcodeBuffer.trim();
      barcodeBuffer = '';
      clearTimeout(barcodeTimer);
      if (barcodeCallback) barcodeCallback(code);
      return;
    }

    if (e.key.length === 1) {
      barcodeBuffer += e.key;
      clearTimeout(barcodeTimer);
      // Reset buffer if no new character within 100ms (human typing is slower)
      barcodeTimer = setTimeout(() => {
        barcodeBuffer = '';
      }, 100);
    }
  };

  window.addEventListener('keydown', handler, { capture: true });
  return () => {
    window.removeEventListener('keydown', handler, { capture: true });
    barcodeCallback = null;
    barcodeBuffer = '';
  };
}

// ============ CUSTOMER DISPLAY ============
let customerDisplayWindow = null;
let displayChannel = null;

export function openCustomerDisplay() {
  const width = 600;
  const height = 400;
  const left = window.screen.width - width;
  const top = 0;

  // Use a data URI with initial HTML to avoid cross-origin issues with about:blank
  const initialHTML = encodeURIComponent(`<!DOCTYPE html><html><head><title>Customer Display</title></head><body style="margin:0;padding:40px;background:#0d0d1a;color:#e0e0e0;font-family:system-ui;text-align:center;"><h2>Customer Display</h2><p>Ready to serve you.</p></body></html>`);

  customerDisplayWindow = window.open(
    `data:text/html,${initialHTML}`, 'DutiLyneCustomerDisplay',
    `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,scrollbars=no`
  );

  if (!customerDisplayWindow) {
    throw new Error('Pop-up blocked. Please allow pop-ups for this site.');
  }

  // Set up BroadcastChannel for communication
  displayChannel = new BroadcastChannel('pharmxai_customer_display');

  // Wait briefly for window to open then render
  setTimeout(() => {
    renderCustomerDisplay({ items: [], total: 0, status: 'ready' });
  }, 300);

  return customerDisplayWindow;
}

export function isCustomerDisplayOpen() {
  return customerDisplayWindow && !customerDisplayWindow.closed;
}

export function closeCustomerDisplay() {
  if (customerDisplayWindow && !customerDisplayWindow.closed) {
    customerDisplayWindow.close();
  }
  customerDisplayWindow = null;
  if (displayChannel) { displayChannel.close(); displayChannel = null; }
}

export function updateCustomerDisplay(data) {
  if (!isCustomerDisplayOpen()) return;
  renderCustomerDisplay(data);
}

function renderCustomerDisplay(data) {
  if (!customerDisplayWindow || customerDisplayWindow.closed) return;

  const branding = getPharmacyBranding();
  const { items = [], total = 0, status = 'ready', message = '' } = data;
  const displayTotal = Number(total) || 0;
  const primaryColor = branding.primary_color || '#14b8a6';

  const itemsHTML = items.map(item => {
    const qty = Number(item.quantity) || 0;
    const price = Number(item.unit_price) || 0;
    return `
    <tr>
      <td style="padding:6px 12px;border-bottom:1px solid #1a1a2e;">${item.product_name || ''}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #1a1a2e;text-align:center;">${qty}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #1a1a2e;text-align:right;">${price.toFixed(2)}</td>
      <td style="padding:6px 12px;border-bottom:1px solid #1a1a2e;text-align:right;font-weight:600;">${(qty * price).toFixed(2)}</td>
    </tr>
  `;
  }).join('');

  let statusHTML = '';
  if (status === 'ready') {
    statusHTML = `<div style="text-align:center;padding:40px;color:#666;font-size:18px;">Welcome! Ready to serve you.</div>`;
  } else if (status === 'scanning') {
    statusHTML = items.length === 0 ? `<div style="text-align:center;padding:40px;color:#666;">Scanning items...</div>` : '';
  } else if (status === 'payment') {
    statusHTML = `<div style="text-align:center;padding:15px;background:${primaryColor};color:white;border-radius:12px;margin:15px;font-size:20px;font-weight:bold;">
      TOTAL: ${displayTotal.toFixed(2)}<br><span style="font-size:14px;font-weight:normal;">${message || 'Processing payment...'}</span>
    </div>`;
  } else if (status === 'complete') {
    statusHTML = `<div style="text-align:center;padding:20px;color:#22c55e;font-size:24px;font-weight:bold;">
      Thank You!<br><span style="font-size:14px;color:#666;font-weight:normal;">Have a great day!</span>
    </div>`;
  }

  const html = `<!DOCTYPE html>
<html><head><title>Customer Display - ${branding.name}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; background: #0d0d1a; color: #e0e0e0; height: 100vh; display: flex; flex-direction: column; }
  .header { background: ${primaryColor}; color: white; padding: 12px 20px; display: flex; align-items: center; justify-content: space-between; }
  .header h1 { font-size: 20px; font-weight: 700; }
  .header .time { font-size: 13px; opacity: 0.85; }
  .content { flex: 1; overflow-y: auto; padding: 0; }
  table { width: 100%; border-collapse: collapse; }
  th { background: #1a1a2e; padding: 8px 12px; text-align: left; font-size: 12px; text-transform: uppercase; color: #888; letter-spacing: 0.5px; }
  th:nth-child(3), th:nth-child(4) { text-align: right; }
  td { font-size: 14px; }
  .footer { background: #111122; padding: 12px 20px; border-top: 2px solid ${primaryColor}; display: flex; justify-content: space-between; align-items: center; }
  .footer .label { font-size: 14px; color: #888; text-transform: uppercase; }
  .footer .amount { font-size: 32px; font-weight: 800; color: ${primaryColor}; }
  .item-count { font-size: 12px; color: #666; }
</style></head><body>
  <div class="header">
    <h1>${branding.name}</h1>
    <div class="time">${new Date().toLocaleTimeString()}</div>
  </div>
  <div class="content">
    ${items.length > 0 ? `
      <table>
        <thead><tr><th>Item</th><th style="text-align:center;">Qty</th><th style="text-align:right;">Price</th><th style="text-align:right;">Amount</th></tr></thead>
        <tbody>${itemsHTML}</tbody>
      </table>
    ` : ''}
    ${statusHTML}
  </div>
  <div class="footer">
    <div>
      <div class="label">Total</div>
      <div class="item-count">${items.length} item${items.length !== 1 ? 's' : ''}</div>
    </div>
    <div class="amount">${displayTotal.toFixed(2)}</div>
  </div>
</body></html>`;

  try {
    customerDisplayWindow.document.open();
    customerDisplayWindow.document.write(html);
    customerDisplayWindow.document.close();
  } catch (e) {
    // Fallback: try setting innerHTML on body directly
    try {
      if (customerDisplayWindow.document && customerDisplayWindow.document.body) {
        customerDisplayWindow.document.body.innerHTML = html;
      }
    } catch (e2) {
      console.warn('Customer display render failed:', e2);
    }
  }
}

// ============ HELPERS ============
function getPharmacyBranding() {
  try {
    const settings = JSON.parse(localStorage.getItem('pharmxai_settings') || '{}');
    return {
      name: settings.business_name || 'DutiLyne',
      address: settings.business_address || '',
      phone: settings.contact_phone || '',
      email: settings.contact_email || '',
      primary_color: settings.primary_color || '#14b8a6',
    };
  } catch {
    return { name: 'DutiLyne', address: '', phone: '', primary_color: '#14b8a6' };
  }
}

// ============ HARDWARE STATUS ============
export function getHardwareStatus() {
  return {
    webSerialSupported: isWebSerialSupported(),
    thermalPrinter: isThermalPrinterConnected(),
    customerDisplay: isCustomerDisplayOpen(),
    barcodeScanner: true,  // Always available (keyboard emulation)
    cashDrawer: isThermalPrinterConnected(), // Requires printer connection
  };
}
